import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const FinalRespectfulEid = () => {
  const mountRef = useRef(null);
  const [showText, setShowText] = useState(false);
  const [userName, setUserName] = useState("");
  const [gender, setGender] = useState("female"); // Default
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!isSubmitted) return;

    const mount = mountRef.current;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020205);

    const camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mount.appendChild(renderer.domElement);

    // --- Elegant Starry Background ---
    const starGeometry = new THREE.BufferGeometry();
    const starVertices = [];
    for (let i = 0; i < 2000; i++) {
      starVertices.push((Math.random() - 0.5) * 50, (Math.random() - 0.5) * 50, (Math.random() - 0.5) * 50);
    }
    starGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starVertices, 3));
    const stars = new THREE.Points(starGeometry, new THREE.PointsMaterial({ color: 0xffffff, size: 0.015 }));
    scene.add(stars);

    // --- Premium Golden Cube ---
    const cube = new THREE.Mesh(
      new THREE.BoxGeometry(1.8, 1.8, 1.8),
      new THREE.MeshStandardMaterial({ 
        color: 0xffd700, 
        metalness: 0.9, 
        roughness: 0.1,
        emissive: 0x221100 
      })
    );
    scene.add(cube);

    const light = new THREE.PointLight(0xffffff, 2, 50);
    light.position.set(5, 5, 5);
    scene.add(light);
    scene.add(new THREE.AmbientLight(0xffffff, 0.3));

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const handleMouseClick = (event) => {
      const rect = mount.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObject(cube);
      if (intersects.length > 0) setShowText(true);
    };

    mount.addEventListener('click', handleMouseClick);

    const animate = () => {
      requestAnimationFrame(animate);
      stars.rotation.y += 0.0003;
      if (cube.visible) {
        cube.rotation.y += 0.015;
        cube.rotation.x += 0.01;
        if (showText) {
          cube.scale.lerp(new THREE.Vector3(0, 0, 0), 0.1);
          if (cube.scale.x < 0.01) cube.visible = false;
        }
      }
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      mount.removeEventListener('click', handleMouseClick);
      mount.removeChild(renderer.domElement);
    };
  }, [isSubmitted, showText]);

  return (
    <div style={{ width: '100%', height: '100vh', background: '#020205', overflow: 'hidden', fontFamily: 'serif' }}>
      
      {!isSubmitted && (
        <div style={styles.formContainer}>
          <h2 style={{ color: '#ffd700', marginBottom: '20px' }}>Personalized Greeting</h2>
          
          <div style={styles.toggleRow}>
            <label style={styles.radioLabel}>
              <input type="radio" name="gender" value="female" checked={gender === 'female'} onChange={() => setGender('female')} />
              <span>Female </span>
            </label>
            <label style={styles.radioLabel}>
              <input type="radio" name="gender" value="male" checked={gender === 'male'} onChange={() => setGender('male')} />
              <span>Male </span>
            </label>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); if(userName.trim()) setIsSubmitted(true); }}>
            <input 
              type="text" 
              placeholder="Enter Name..." 
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              style={styles.inputField}
              required
              autoFocus
            />
            <button type="submit" style={styles.submitBtn}>Create Surprise ✧</button>
          </form>
        </div>
      )}

      {isSubmitted && (
        <div ref={mountRef} style={{ width: '100%', height: '100vh', position: 'relative' }}>
          {!showText && (
            <div style={styles.instruction}>✦ CLICK THE GIFT TO REVEAL ✦</div>
          )}

          {showText && (
            <div style={styles.textContainer}>
              <p style={styles.topLine}>Eid Mubarak to  dear,</p>
              <h1 style={styles.mainHeading}>
                {gender === 'female' ? 'Mam ' : 'Mr. '}{userName.toUpperCase()}
              </h1>
              <div style={styles.goldLine}></div>
              <p style={styles.message}>
                Wishing you a blessed Eid filled with peace, <br/> prosperity, and happiness.
              </p>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeInBlur {
          from { opacity: 0; transform: translate(-50%, -45%); filter: blur(10px); }
          to { opacity: 1; transform: translate(-50%, -50%); filter: blur(0); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  formContainer: {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    textAlign: 'center', width: '320px', padding: '30px', background: 'rgba(255,255,255,0.03)',
    borderRadius: '15px', border: '1px solid rgba(255,215,0,0.2)'
  },
  toggleRow: { display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px' },
  radioLabel: { color: '#fff', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' },
  inputField: {
    padding: '12px', width: '100%', borderRadius: '8px', border: '1px solid #ffd700',
    background: 'transparent', color: '#fff', textAlign: 'center', outline: 'none', marginBottom: '15px'
  },
  submitBtn: {
    padding: '12px', borderRadius: '25px', border: 'none', background: '#ffd700',
    color: '#000', fontWeight: 'bold', cursor: 'pointer', width: '100%'
  },
  instruction: {
    position: 'absolute', bottom: '15%', width: '100%', textAlign: 'center',
    color: '#ffd700', letterSpacing: '4px', opacity: 0.6
  },
  textContainer: {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    textAlign: 'center', width: '90%', animation: 'fadeInBlur 1.5s ease-out forwards'
  },
  topLine: { color: '#ffffff', fontSize: '1.1rem', fontStyle: 'italic', margin: 0 },
  mainHeading: { 
    color: '#ffd700', fontSize: 'clamp(2.2rem, 8vw, 5rem)', margin: '10px 0', 
    textShadow: '0 0 30px rgba(255, 215, 0, 0.4)', fontWeight: 'bold' 
  },
  goldLine: { height: '1px', width: '100px', background: '#ffd700', margin: '20px auto' },
  message: { color: '#ccc', fontSize: '1.1rem', letterSpacing: '1.5px', lineHeight: '1.6' }
};

export default FinalRespectfulEid;